import React, { useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface XPCounterProps {
  totalXP: number;
  readinessPercentage: number;
  isExpanded: boolean;
  onToggle: () => void;
}

export const XPCounter: React.FC<XPCounterProps> = ({
  totalXP,
  readinessPercentage,
  isExpanded,
  onToggle,
}) => {
  const progress = useMemo(() => {
    if (Number.isNaN(readinessPercentage)) {
      return 0;
    }

    return Math.min(Math.max(readinessPercentage, 0), 100);
  }, [readinessPercentage]);

  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-3 rounded-full border border-neutral-200 bg-white px-3 py-2 shadow-sm transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      aria-label="Toggle AI Readiness details"
    >
      <div className="relative h-12 w-12">
        <svg className="h-12 w-12 rotate-[-90deg]" viewBox="0 0 48 48" aria-hidden="true">
          <circle
            className="text-neutral-200"
            strokeWidth="4"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx="24"
            cy="24"
          />
          <circle
            className="text-primary"
            strokeWidth="4"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx="24"
            cy="24"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-semibold text-neutral-800">{Math.round(progress)}%</span>
        </div>
      </div>

      <div className="flex flex-col items-start leading-tight text-left">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">XP</span>
        <span className="text-sm font-bold text-neutral-800">{totalXP.toLocaleString()} XP</span>
      </div>

      <span className="text-neutral-500" aria-hidden="true">
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </span>
    </button>
  );
};

export default XPCounter;
